import routingService from 'common/src/modules/routing/routing.service';
import {RootState} from 'common/src/redux/store';
import {z} from 'zod';
import {ToolName} from '../../copilot.enums';
import {ToolExecuteParams, ToolResult} from '../../copilot.types';
import tool from '../../utils/creators/tool';
import toolResult from '../../utils/creators/toolResult';
import toolError from '../../utils/toolError';
import {lowConfidenceThreshold} from './generalNavigationTool.consts';
import buildAppRoute from './utils/buildAppRoute';
import promptToRoute from './utils/promptToRoute/promptToRoute';
import validateAppRoute from './utils/validateAppRoute';
import waitForNavigationCompletion from './utils/waitForNavigationCompletion';

const inputSchema = z.object({
  prompt: z
    .string()
    .describe(
      'Natural language page navigation request. Must describe a destination page/route to navigate to (e.g. "go to campaigns library", "open settings page"). Do NOT use for in-page actions like arranging, editing, or modifying content.',
    ),
});

function generalNavigationTool(getState: () => RootState) {
  async function execute({
    input: {prompt},
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const promptToRouteResult = await promptToRoute(prompt, toolUtils, callChain, getState);
      const {routeKey, pathParamValues, queryParamValues, confidence, reasoning} =
        promptToRouteResult;

      if (!promptToRouteResult.success || !routeKey) {
        const {missingInfo} = promptToRouteResult;

        return toolError(
          `Could not navigate to the requested route. The following information was missing: ${missingInfo}`,
        );
      }

      if (confidence < lowConfidenceThreshold) {
        return toolError(
          `The navigation request was too vague to resolve confidently. Reasoning: ${reasoning}. Please specify the destination page more clearly.`,
        );
      }

      const routeDef = routingService.getRouteDefinition(routeKey);

      const appRoute = buildAppRoute({
        routeDef,
        pathParamValues,
        queryParamValues,
        logger: toolUtils.logger,
      });

      const errorMessage = validateAppRoute({appRoute, routeDef});

      if (errorMessage) {
        return toolError(`Error resolving route: ${errorMessage}`);
      }

      routingService.goTo(appRoute);

      const navigationResult = await waitForNavigationCompletion({
        getState,
        isNavigationComplete: routeDef.isNavigationComplete ?? (() => true),
        logger: toolUtils.logger,
      });

      if (!navigationResult.passed) {
        return toolError(`Navigation failed: ${navigationResult.reason}`);
      }

      const url = routingService.createUrl(appRoute);

      return toolResult(`Successfully navigated to ${routeDef.displayName} (${url}).`);
    } catch (error) {
      return toolError(
        `Navigation failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
      );
    }
  }

  return tool({
    name: ToolName.GeneralNavigation,
    displayName: 'Navigating',
    description:
      'Navigate to a different page or route in the platform. Only for changing the current page/URL — NOT for in-page actions like auto-arranging cards, editing content, or triggering UI operations.',
    execute,
    inputSchema,
  });
}

export default generalNavigationTool;
